import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  fetchDataCategory,
  updateDataCategory,
  fetchDataTypes,
  fetchCustomerCategories,
  fetchProductCategories,
  fetchMemberCategories,
  fetchPartnerCategories,
  fetchApplicationCategories,
  fetchItCategories,
  fetchFinancialAssetCategories,
  fetchPlaceCategories,
  fetchDepartments,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function DataCategoryEditPage() {
  const { businessDomainId, businessUnitId, categoryId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/data-categories`;

  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState({ dataCategoryName: "", dataCategoryDescription: "", url: "" });
  const [dataTypes, setDataTypes] = useState([]);
  const [customerCategories, setCustomerCategories] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [memberCategories, setMemberCategories] = useState([]);
  const [partnerCategories, setPartnerCategories] = useState([]);
  const [applicationCategories, setApplicationCategories] = useState([]);
  const [itCategories, setItCategories] = useState([]);
  const [financialAssetCategories, setFinancialAssetCategories] = useState([]);
  const [placeCategories, setPlaceCategories] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [selectedDataTypeId, setSelectedDataTypeId] = useState("");
  const [selectedCustomerCategoryId, setSelectedCustomerCategoryId] = useState("");
  const [selectedProductCategoryId, setSelectedProductCategoryId] = useState("");
  const [selectedMemberCategoryId, setSelectedMemberCategoryId] = useState("");
  const [selectedPartnerCategoryId, setSelectedPartnerCategoryId] = useState("");
  const [selectedApplicationCategoryId, setSelectedApplicationCategoryId] = useState("");
  const [selectedItCategoryId, setSelectedItCategoryId] = useState("");
  const [selectedFinancialAssetCategoryId, setSelectedFinancialAssetCategoryId] = useState("");
  const [selectedPlaceCategoryId, setSelectedPlaceCategoryId] = useState("");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [selectedBusinessState, setSelectedBusinessState] = useState("Planned");

  const loadData = useCallback(async () => {
    try {
      const [d, types, customers, products, members, partners, apps, its, fins, places, depts] = await Promise.all([
        fetchDataCategory(categoryId),
        fetchDataTypes(businessDomainId),
        fetchCustomerCategories(businessUnitId),
        fetchProductCategories(businessUnitId),
        fetchMemberCategories(COMPANY_ID),
        fetchPartnerCategories(COMPANY_ID),
        fetchApplicationCategories(COMPANY_ID),
        fetchItCategories(COMPANY_ID),
        fetchFinancialAssetCategories(COMPANY_ID),
        fetchPlaceCategories(COMPANY_ID),
        fetchDepartments(COMPANY_ID),
      ]);
      setOriginal(d);
      setForm({
        dataCategoryName: d.dataCategoryName || "",
        dataCategoryDescription: d.dataCategoryDescription || "",
        url: d.url || "",
      });
      setDataTypes(Array.isArray(types) ? types : []);
      setCustomerCategories(Array.isArray(customers) ? customers : []);
      setProductCategories(Array.isArray(products) ? products : []);
      setMemberCategories(Array.isArray(members) ? members : []);
      setPartnerCategories(Array.isArray(partners) ? partners : []);
      setApplicationCategories(Array.isArray(apps) ? apps : []);
      setItCategories(Array.isArray(its) ? its : []);
      setFinancialAssetCategories(Array.isArray(fins) ? fins : []);
      setPlaceCategories(Array.isArray(places) ? places : []);
      setDepartments(Array.isArray(depts) ? depts : []);
      // 初期選択値
      setSelectedDataTypeId(d.dataTypeId || "");
      setSelectedCustomerCategoryId(d.customerCategoryId || "");
      setSelectedProductCategoryId(d.productCategoryId || "");
      setSelectedMemberCategoryId(d.memberCategoryId || "");
      setSelectedPartnerCategoryId(d.partnerCategoryId || "");
      setSelectedApplicationCategoryId(d.applicationCategoryId || "");
      setSelectedItCategoryId(d.itCategoryId || "");
      setSelectedFinancialAssetCategoryId(d.financialAssetCategoryId || "");
      setSelectedPlaceCategoryId(d.placeCategoryId || "");
      setSelectedDepartmentId(d.departmentId || "");
      setSelectedBusinessState(d.businessState || "Planned");
    } catch (e) { notify.apiError(e); }
  }, [categoryId, businessDomainId, businessUnitId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!original) return;
    const dt = dataTypes.find((d) => d.dataTypeId === selectedDataTypeId);
    // selectedDataTypeId が選択されている場合は associatedDataTypes を自動更新
    const associatedDataTypes = selectedDataTypeId && dt
      ? [{ dataTypeId: selectedDataTypeId, dataTypeName: dt.dataTypeName, dataAccessType: "None" }]
      : (original.associatedDataTypes || []);
    try {
      await updateDataCategory(categoryId, {
        ...original,
        ...form,
        dataTypeId: selectedDataTypeId || original.dataTypeId,
        dataTypeName: dt?.dataTypeName || original.dataTypeName || "",
        associatedDataTypes,
        customerCategoryId: selectedCustomerCategoryId || null,
        productCategoryId: selectedProductCategoryId || null,
        memberCategoryId: selectedMemberCategoryId || null,
        partnerCategoryId: selectedPartnerCategoryId || null,
        applicationCategoryId: selectedApplicationCategoryId || null,
        itCategoryId: selectedItCategoryId || null,
        financialAssetCategoryId: selectedFinancialAssetCategoryId || null,
        placeCategoryId: selectedPlaceCategoryId || null,
        departmentId: selectedDepartmentId || null,
        businessState: selectedBusinessState,
      });
      notify.success(t("message.update.success"));
      navigate(`${basePath}/${categoryId}`);
    } catch (err) { notify.apiError(err); }
  };

  const SelectField = ({ label, value, onChange, options, idKey, nameKey, required }) => (
    <FormControl fullWidth required={required}>
      <InputLabel>{label}</InputLabel>
      <Select value={value} onChange={onChange} label={label}>
        {!required && <MenuItem value=""><em>—</em></MenuItem>}
        {options.map((o) => (
          <MenuItem key={o[idKey]} value={o[idKey]}>{o[nameKey]}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("dataCategory.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <SelectField label={t("dataCategory.dataTypeName")} value={selectedDataTypeId}
              onChange={(e) => setSelectedDataTypeId(e.target.value)}
              options={dataTypes} idKey="dataTypeId" nameKey="dataTypeName" />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth required label={t("dataCategory.name")}
              name="dataCategoryName" value={form.dataCategoryName} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline rows={3} label={t("common.description")}
              name="dataCategoryDescription" value={form.dataCategoryDescription} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth label={t("common.url")}
              name="url" value={form.url} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <SelectField label={t("dataCategory.customerCategoryName")} value={selectedCustomerCategoryId}
              onChange={(e) => setSelectedCustomerCategoryId(e.target.value)}
              options={customerCategories} idKey="customerCategoryId" nameKey="customerCategoryName" />
          </Grid>
          <Grid size={12}>
            <SelectField label={t("dataCategory.productCategoryName")} value={selectedProductCategoryId}
              onChange={(e) => setSelectedProductCategoryId(e.target.value)}
              options={productCategories} idKey="productCategoryId" nameKey="productCategoryName" />
          </Grid>
          <Grid size={12}>
            <SelectField label={t("dataCategory.memberCategoryName")} value={selectedMemberCategoryId}
              onChange={(e) => setSelectedMemberCategoryId(e.target.value)}
              options={memberCategories} idKey="memberCategoryId" nameKey="memberCategoryName" />
          </Grid>
          <Grid size={12}>
            <SelectField label={t("dataCategory.partnerCategoryName")} value={selectedPartnerCategoryId}
              onChange={(e) => setSelectedPartnerCategoryId(e.target.value)}
              options={partnerCategories} idKey="partnerCategoryId" nameKey="partnerCategoryName" />
          </Grid>
          <Grid size={12}>
            <SelectField label={t("dataCategory.applicationCategoryName")} value={selectedApplicationCategoryId}
              onChange={(e) => setSelectedApplicationCategoryId(e.target.value)}
              options={applicationCategories} idKey="applicationCategoryId" nameKey="applicationCategoryName" />
          </Grid>
          <Grid size={12}>
            <SelectField label={t("dataCategory.itCategoryName")} value={selectedItCategoryId}
              onChange={(e) => setSelectedItCategoryId(e.target.value)}
              options={itCategories} idKey="itCategoryId" nameKey="itCategoryName" />
          </Grid>
          <Grid size={12}>
            <SelectField label={t("dataCategory.financialAssetCategoryName")} value={selectedFinancialAssetCategoryId}
              onChange={(e) => setSelectedFinancialAssetCategoryId(e.target.value)}
              options={financialAssetCategories} idKey="financialAssetCategoryId" nameKey="financialAssetCategoryName" />
          </Grid>
          <Grid size={12}>
            <SelectField label={t("dataCategory.placeCategoryName")} value={selectedPlaceCategoryId}
              onChange={(e) => setSelectedPlaceCategoryId(e.target.value)}
              options={placeCategories} idKey="placeCategoryId" nameKey="placeCategoryName" />
          </Grid>
          <Grid size={12}>
            <SelectField label={t("dataCategory.departmentName")} value={selectedDepartmentId}
              onChange={(e) => setSelectedDepartmentId(e.target.value)}
              options={departments} idKey="departmentId" nameKey="departmentName" />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("dataCategory.businessState")}</InputLabel>
              <Select value={selectedBusinessState} onChange={(e) => setSelectedBusinessState(e.target.value)}
                label={t("dataCategory.businessState")}>
                <MenuItem value="Planned">{t("businessState.Planned")}</MenuItem>
                <MenuItem value="Executing">{t("businessState.Executing")}</MenuItem>
                <MenuItem value="Completed">{t("businessState.Completed")}</MenuItem>
                <MenuItem value="Evaluated">{t("businessState.Evaluated")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/data-categories/${categoryId}`)}>{t("common.cancel")}</Button>
        </Box>
      </Box>
    </Box>
  );
}
